import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Trophy, Users, ChevronRight, Facebook, ExternalLink, BarChart2 } from "lucide-react";

export const LeagueHomeSection = ({ league, standings, games, teams, onTabChange }) => {
  const [selectedDivision, setSelectedDivision] = useState("all");

  const divisions = league.divisions || [];
  const conferences = league.conferences || [];

  // Facebook feed would be embedded via iframe - no need for loading state
  const hasFacebookPage = !!league.facebook_page_id;

  // Re-parse Facebook widgets when component mounts or facebook_page_id changes
  useEffect(() => {
    if (hasFacebookPage && window.FB) {
      window.FB.XFBML.parse();
    }
  }, [hasFacebookPage, league.facebook_page_id]);

  // Filter standings by division
  const getFilteredStandings = () => {
    if (selectedDivision === "all") {
      return standings.slice(0, 10); // Show top 10
    }
    return standings.filter(s => {
      const divId = s.division_entry?.division_id || s.division?.id;
      return divId === selectedDivision;
    }).slice(0, 10);
  };

  // Get upcoming games (next 5)
  const upcomingGames = (games || [])
    .filter(g => !g.is_completed && new Date(g.scheduled_date) >= new Date())
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
    .slice(0, 5);

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "TBD";
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getTeamById = (teamId) => {
    return teams?.find(t => t.id === teamId);
  };

  const filteredStandings = getFilteredStandings();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content - 2 columns */}
      <div className="lg:col-span-2 space-y-6">
        {/* League Welcome */}
        <div 
          className="p-6 rounded-xl border border-zinc-800"
          style={{ backgroundColor: `${league.color}10` }}
        >
          <div className="flex items-center gap-4 mb-4">
            {league.logo_url && (
              <img src={league.logo_url} alt={league.name} className="w-16 h-16 object-contain" />
            )}
            <div>
              <h1 className="font-heading text-2xl uppercase text-white tracking-wide">
                {league.name}
              </h1>
              {league.season && (
                <p className="text-zinc-400 text-sm">{league.season} Season</p>
              )}
            </div>
          </div>
          {league.description && (
            <p className="text-zinc-300 text-sm leading-relaxed">{league.description}</p>
          )}
          
          {/* Quick Stats */}
          <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-zinc-500" />
              <span className="text-white font-medium">{teams?.length || 0}</span>
              <span className="text-zinc-500 text-sm">Teams</span>
            </div>
            {conferences.length > 0 && (
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-zinc-500" />
                <span className="text-white font-medium">{conferences.length}</span>
                <span className="text-zinc-500 text-sm">Conferences</span>
              </div>
            )}
            {divisions.length > 0 && (
              <div className="flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-zinc-500" />
                <span className="text-white font-medium">{divisions.length}</span>
                <span className="text-zinc-500 text-sm">Divisions</span>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Games */}
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" style={{ color: league.color }} />
              <h2 className="font-heading text-lg uppercase text-white tracking-wide">Upcoming Games</h2>
            </div>
            <button 
              onClick={() => onTabChange?.("schedule")}
              className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"
              data-testid="view-all-schedule-btn"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="divide-y divide-zinc-800">
            {upcomingGames.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No upcoming games scheduled</p>
              </div>
            ) : (
              upcomingGames.map(game => {
                const homeTeam = getTeamById(game.home_team_id) || game.home_team;
                const awayTeam = getTeamById(game.away_team_id) || game.away_team;
                
                return (
                  <div key={game.id} className="p-4 hover:bg-zinc-800/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        {/* Teams */}
                        <div className="flex items-center gap-3 mb-2">
                          {/* Away Team */}
                          <div className="flex items-center gap-2 flex-1">
                            {awayTeam?.logo_url ? (
                              <img src={awayTeam.logo_url} alt="" className="w-8 h-8 object-contain" />
                            ) : (
                              <div 
                                className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold text-white"
                                style={{ backgroundColor: awayTeam?.color || "#333" }}
                              >
                                {awayTeam?.abbreviation || "?"}
                              </div>
                            )}
                            <span className="text-zinc-300 text-sm">{awayTeam?.name || "TBD"}</span>
                          </div>
                          
                          <span className="text-zinc-600 text-xs">@</span>
                          
                          {/* Home Team */}
                          <div className="flex items-center gap-2 flex-1 justify-end">
                            <span className="text-white font-medium text-sm">{homeTeam?.name || "TBD"}</span>
                            {homeTeam?.logo_url ? (
                              <img src={homeTeam.logo_url} alt="" className="w-8 h-8 object-contain" />
                            ) : (
                              <div 
                                className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold text-white"
                                style={{ backgroundColor: homeTeam?.color || "#333" }}
                              >
                                {homeTeam?.abbreviation || "?"}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Date/Time/Location */}
                        <div className="flex items-center gap-4 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {game.date_tbd ? "Date TBD" : formatDate(game.scheduled_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {game.time_tbd ? "Time TBD" : formatTime(game.scheduled_time)}
                          </span>
                          {game.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {game.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Social Feed */}
        {hasFacebookPage && (
          <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Facebook className="w-5 h-5 text-blue-500" />
                <h2 className="font-heading text-lg uppercase text-white tracking-wide">Social Feed</h2>
              </div>
              <a 
                href={`https://facebook.com/${league.facebook_page_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"
              >
                Follow <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {/* Facebook Page Plugin Embed */}
                <div 
                  className="fb-page" 
                  data-href={`https://www.facebook.com/${league.facebook_page_id}`}
                  data-tabs="timeline"
                  data-width="500"
                  data-height="400"
                  data-small-header="true"
                  data-adapt-container-width="true"
                  data-hide-cover="false"
                  data-show-facepile="false"
                >
                  <blockquote 
                    cite={`https://www.facebook.com/${league.facebook_page_id}`} 
                    className="fb-xfbml-parse-ignore"
                  >
                    <a href={`https://www.facebook.com/${league.facebook_page_id}`}>
                      Follow us on Facebook
                    </a>
                  </blockquote>
                </div>
                
                {/* Fallback link if embed doesn't load */}
                <div className="text-center pt-4 border-t border-zinc-800">
                  <a 
                    href={`https://facebook.com/${league.facebook_page_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="w-4 h-4" />
                    View on Facebook
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar - Standings */}
      <div className="space-y-6">
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden sticky top-4">
          <div className="px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5" style={{ color: league.color }} />
                <h2 className="font-heading text-lg uppercase text-white tracking-wide">Standings</h2>
              </div>
            </div>
            
            {/* Division Filter */}
            {divisions.length > 0 && (
              <select
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
                data-testid="standings-division-select"
              >
                <option value="all">All Divisions</option>
                {divisions.map(div => (
                  <option key={div.id} value={div.id}>{div.name}</option>
                ))}
              </select>
            )}
          </div>
          
          <div className="divide-y divide-zinc-800">
            {filteredStandings.length === 0 ? (
              <div className="p-6 text-center text-zinc-500">
                <BarChart2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No standings available</p>
              </div>
            ) : (
              filteredStandings.map((standing, idx) => {
                const team = standing.team;
                const displayName = standing.display_name || standing.division_entry?.display_name || team?.name || "Unknown";
                
                return (
                  <div 
                    key={standing.division_entry_id || standing.team_id || idx}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/30 transition-colors"
                  >
                    <span className="text-zinc-500 text-sm font-mono w-5">{idx + 1}</span>
                    {team?.logo_url ? (
                      <img src={team.logo_url} alt="" className="w-6 h-6 object-contain" />
                    ) : (
                      <div 
                        className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: team?.color || "#333" }}
                      >
                        {team?.abbreviation?.charAt(0) || "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/team/${team?.slug || team?.id}`}
                        state={{ 
                          from: "league", 
                          tab: "home", 
                          leagueSlug: league.slug,
                          leagueName: league.name 
                        }}
                        className="text-white text-sm font-medium hover:text-orange-400 truncate block"
                      >
                        {displayName}
                      </Link>
                    </div>
                    <div className="text-right">
                      <span className="text-white text-sm font-medium">{standing.wins}-{standing.losses}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <div className="px-4 py-3 border-t border-zinc-800">
            <button 
              onClick={() => onTabChange?.("standings")}
              className="w-full text-sm text-zinc-400 hover:text-white flex items-center justify-center gap-1"
              data-testid="view-full-standings-btn"
            >
              View Full Standings <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
